import { useEffect, useRef } from 'react'
import L from 'leaflet'
import type { POIElement } from '../types'

interface UsePOIMarkersProps {
  mapRef: React.MutableRefObject<L.Map | null>
  poiOverlayRef: React.MutableRefObject<HTMLDivElement | null>
  isInitializedRef: React.MutableRefObject<boolean>
  nearbyPOIs: Array<{ lat: number; lon: number; title: string; key: string }>
  poiPosition: { lat: number; lon: number; title: string } | null
  onPOIMarkerClick?: (poi: { key: string; lat: number; lon: number; title: string }) => void
  updateOverlayPositions: () => void
  poiElementsRef: React.MutableRefObject<Map<string, POIElement>>
}

export const usePOIMarkers = ({
  mapRef,
  poiOverlayRef,
  isInitializedRef,
  nearbyPOIs,
  poiPosition,
  onPOIMarkerClick,
  updateOverlayPositions,
  poiElementsRef,
}: UsePOIMarkersProps) => {
  const poiMarkerRef = useRef<L.CircleMarker | null>(null)
  const lastPOIsRef = useRef<string>('')

  // Manage nearby POI elements
  useEffect(() => {
    if (!poiOverlayRef.current || !mapRef.current || !isInitializedRef.current) return

    try {
      const container = mapRef.current.getContainer()
      if (!container || !container.offsetParent) {
        return
      }
    } catch (error) {
      return
    }

    const currentPOIsKey = nearbyPOIs.map(poi => `${poi.key}:${poi.lat}:${poi.lon}`).sort().join('|')

    if (currentPOIsKey === lastPOIsRef.current) {
      return
    }
    lastPOIsRef.current = currentPOIsKey

    // Remove POI elements that are no longer in the list
    const currentKeys = new Set(nearbyPOIs.map(poi => poi.key))
    poiElementsRef.current.forEach((poiData, key) => {
      if (!currentKeys.has(key)) {
        poiData.element.remove()
        poiElementsRef.current.delete(key)
      }
    })

    // Add or update POI elements
    nearbyPOIs.forEach((poi) => {
      if (poiPosition &&
        Math.abs(poi.lat - poiPosition.lat) < 0.0001 &&
        Math.abs(poi.lon - poiPosition.lon) < 0.0001) {
        return
      }

      const existingPOI = poiElementsRef.current.get(poi.key)

      if (!existingPOI) {
        const poiElement = document.createElement('div')
        poiElement.className = 'poi-marker-overlay'
        
        // Calculate initial position immediately
        if (mapRef.current) {
          const initialPoint = mapRef.current.latLngToContainerPoint([poi.lat, poi.lon])
        
          poiElement.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 16px;
            height: 16px;
            margin-left: -8px;
            margin-top: -8px;
            border-radius: 50%;
            background-color: #ff9500;
            border: 2px solid #fff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            cursor: pointer;
            z-index: 1000;
            transition: box-shadow 0.2s;
            display: block;
            transform: translate(${initialPoint.x}px, ${initialPoint.y}px);
            will-change: transform;
          `
        }

        poiElement.style.pointerEvents = 'auto'

        const poiRef = poi

        poiElement.addEventListener('mouseenter', () => {
          if (!mapRef.current) return
          try {
            const point = mapRef.current.latLngToContainerPoint([poiRef.lat, poiRef.lon])
            poiElement.style.transform = `translate(${point.x}px, ${point.y}px) scale(1.3)`
            poiElement.style.boxShadow = '0 3px 6px rgba(0,0,0,0.5)'
          } catch (error) {
            // Ignore
          }
        })
        poiElement.addEventListener('mouseleave', () => {
          if (!mapRef.current) return
          try {
            const point = mapRef.current.latLngToContainerPoint([poiRef.lat, poiRef.lon])
            poiElement.style.transform = `translate(${point.x}px, ${point.y}px) scale(1)`
            poiElement.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)'
          } catch (error) {
            // Ignore
          }
        })

        poiElement.addEventListener('click', () => {
          if (onPOIMarkerClick) {
            onPOIMarkerClick(poiRef)
          }
        })

        poiElement.title = poi.title || 'Point of Interest'

        poiOverlayRef.current?.appendChild(poiElement)
        poiElementsRef.current.set(poi.key, {
          element: poiElement,
          lat: poi.lat,
          lon: poi.lon,
          title: poi.title,
        })
      } else {
        if (Math.abs(existingPOI.lat - poi.lat) > 0.0001 ||
          Math.abs(existingPOI.lon - poi.lon) > 0.0001 ||
          existingPOI.title !== poi.title) {
          existingPOI.lat = poi.lat
          existingPOI.lon = poi.lon
          existingPOI.title = poi.title
          existingPOI.element.title = poi.title || 'Point of Interest'
        }
      }
    })

    updateOverlayPositions()
  }, [nearbyPOIs, poiPosition, onPOIMarkerClick, updateOverlayPositions, mapRef, poiOverlayRef, isInitializedRef, poiElementsRef])

  // Manage selected POI marker (highlighted)
  useEffect(() => {
    if (!mapRef.current || !isInitializedRef.current) return

    try {
      const container = mapRef.current.getContainer()
      if (!container || !container.offsetParent) {
        return
      }
    } catch (error) {
      return
    }

    if (!poiPosition) {
      if (poiMarkerRef.current) {
        try {
          mapRef.current.removeLayer(poiMarkerRef.current)
        } catch (error) {
          // Ignore
        }
        poiMarkerRef.current = null
      }
      return
    }

    const ll: [number, number] = [poiPosition.lat, poiPosition.lon]

    if (!poiMarkerRef.current) {
      try {
        poiMarkerRef.current = L.circleMarker(ll, {
          radius: 12,
          color: '#ff5a5a',
          fillColor: '#ff5a5a',
          fillOpacity: 1,
          weight: 4,
          className: 'poi-marker-selected',
        }).addTo(mapRef.current)

        poiMarkerRef.current.bindTooltip(poiPosition.title || 'Point of Interest', {
          permanent: false,
          direction: 'top',
          className: 'poi-tooltip',
          offset: [0, -10],
        })
      } catch (error) {
        console.warn('Error adding POI marker:', error)
      }
    } else {
      try {
        poiMarkerRef.current.setLatLng(ll)
        const tooltip = poiMarkerRef.current.getTooltip()
        if (tooltip) {
          poiMarkerRef.current.setTooltipContent(poiPosition.title || 'Point of Interest')
        } else {
          poiMarkerRef.current.bindTooltip(poiPosition.title || 'Point of Interest', {
            permanent: false,
            direction: 'top',
            className: 'poi-tooltip',
            offset: [0, -10],
          })
        }
      } catch (error) {
        console.warn('Error updating POI marker:', error)
      }
    }
  }, [poiPosition, mapRef, isInitializedRef])
}
